/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */

import {
  Component, EventEmitter, Input, OnInit, Output,
} from '@angular/core';

import { ClassicEditor, Editor, EditorConfig } from 'ckeditor5';

// eslint-disable-next-line import/no-extraneous-dependencies

import { AppConfigService } from '../../common/services/app-config/app-config.service';
import { TranslationService } from '../../common/translation.service';
import { EditorUtil } from './editor.util';
import editorConfig from '../editorconfig.constant';

const toolBar = [
  'bold',
  'italic',
  'underline',
  '|',
  'bulletedList',
  'numberedList',
];
/**
 * Component for our CKEditor instance.
 * @example
 * <bulb-editor *ngIf="isTabOpen"
 *        [data]="aboutMeSubSectionClone.description" // data which will contains value of element
 *        [language]="'en'"  // pass user language
 *        [editable] = "true" // possible value true or false
 *        name="description"
 *        id="aboutMeEditor"
 *        [(ngModel)]="aboutMeSubSectionClone.description" // is it required when mapping with form
 *        (dataChange) = "changeData($event)" // will call when data change for editor.
 *        [placeholder] = "editor placeholder"
 *        [toolbarTopPosition] = "10" // top position of editor toolbar on scroll
 *        [toolBarConfig] = "[]" // Array of toolbar button eg: ['bold', 'italic', 'underline'
 *        >
 *        </bulb-editor>
 */
@Component({
  selector: 'bulb-editor',
  templateUrl: './editor.component.html',
  standalone: false,
})
export class EditorComponent implements OnInit {
  /**
   * Debug variable. Used for inspecting the cke editor instance.
   */
  private inspectEditor: boolean = this.appConfigService.editor.inspect;

  /**
   * Editor data property. Should only be updated by initData.
   * Set to public as the AOT compiler complained.
   */
  public data: any;

  /**
   * Sets the editor data if it has not been set already.
   * @param initData to set the data.
   */
  @Input() set initData(initData: any) {
    if (initData && this.data === undefined) {
      this.data = initData;
    }
  }

  /**
   * If the editor is editable.
   */
  @Input() editable: boolean;

  /**
   * Selected editor language.
   */
  @Input() language: string;

  /**
   * update editor placeholder.
   */
  @Input() placeHolder ? : string;

  /**
   * Toolbar position value.
   */
  @Input() toolbarTopPosition ? : number;

  /**
   * Toolbar configuration of editor.
   */
  @Input() toolBarConfig ? : Array<string>;

  /**
   * Save changed data.
   */
  @Output() dataChange = new EventEmitter<string>();

  /**
   * Fire when the character limit reached.
   */
  @Output() limitReached = new EventEmitter<string>();

  /**
   * Number of maximum character allowed in the editor.
   */
  @Input() maxLength: number = -1;

  /**
   * Set the initial focus in editor.
   */
  @Input() autofocus = false;

  /**
   * Current character count.
   */
  characterCount: number = 0;

  /**
   * flag for max length.
   */
  isMaxlengthReached: boolean;

  /**
   * flag for showing/hiding character counter in editor.
   */
  showCharacterCounter: boolean = false;

  /**
   * Editor instance.
   */
  public classicEditorBuild = ClassicEditor;

  /**
   * Lock id
   */
  featureLockId = Symbol('viewMode');

  /**
   * Default Config object for our ckeditor instance.
   */
  public defaultConfig : EditorConfig = {
    root: {
      placeholder: '',
    },
    extraPlugins: [],
    language: 'en',
    ui: {
      viewportOffset: { top: 0 },
    },
  };

  /**
   * Default Constructor.
   *
   * @param $translate Our Angular Translation service.
   * @param appConfigService configuration service for application.
   */
  constructor(
    private $translate: TranslationService,
    private appConfigService: AppConfigService,
  ) {
  }

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * To handle any additional initialization tasks.
   */
  ngOnInit(): void {
    this.setupEditor();
    this.defaultConfig.language = this.language;

    if (this.placeHolder) {
      this.defaultConfig.root = {
        ...this.defaultConfig.root,
        placeholder: this.placeHolder,
      };
    }
    EditorUtil.addTranslations(this.$translate, this.defaultConfig);

    this.defaultConfig.toolbar = {
      items: this.toolBarConfig || toolBar,
    };
    if (this.toolbarTopPosition) {
      this.defaultConfig.ui.viewportOffset = { top: this.toolbarTopPosition };
    }

    if (!globalThis.CKEBulbData) {
      globalThis.CKEBulbData = {
        page: {},
        CONFIG: {},
        readOnly: {},
        service: {},
      };
    }
  }

  /**
   * Fired when the editor is ready. It corresponds with the editor#ready event.
   * It is fired with the editor instance.
   *
   * Upon the initialization of the editor, we handle a few key aspects:
   * - Set up the `autosave` logic
   * - Handle setting the correct data on the editor.
   *
   * @param editor Editor instance.
   */
  public onReady(editor) {
    this.initEditor(editor);
  }

  setupEditor() {
    this.classicEditorBuild = ClassicEditor;
    Object.assign(this.defaultConfig, editorConfig.classicConfig);
  }

  /**
   * Method to initialize the ckeditor.
   * Initialize the editor with the page content data.
   *
   * @param editor CKEditor instance.
   * @private
   */
  private initEditor(editor : Editor) {
    // If we are debugging / inspecting the cke instance, lets attach the inspector to the editor instance:
    if (this.inspectEditor) {
      EditorUtil.attachEditor(editor);
    }

    editor.model.document.on('change:data', () => {
      this.dataChange.emit(editor.getData());
    });

    if (this.maxLength > 0 && this.editable) {
      this.enableCharacterCounter(editor);
    }

    // Enable read-only mode if the user is not logged in -- i.e Anonymous users:
    if (!this.editable) {
      // TODO CG:
      const toolbarElement = editor.ui.view.element?.querySelector('.ck-toolbar') as HTMLElement;
      toolbarElement.style.display = 'none';
      editor.enableReadOnlyMode(this.featureLockId);
    } else if (this.autofocus) {
      editor.editing.view.focus();
    }
  }

  /**
   * Method for show/hide character counter.
   * @param editor
   * @private
   */
  private enableCharacterCounter(editor: Editor) {
    this.showCharacterCounter = true;
    const wordCountPlugin = editor.plugins.get('WordCount');
    this.characterCount = wordCountPlugin.characters;

    wordCountPlugin.on('update', (evt, stats) => {
      this.characterCount = stats.characters;
      this.isMaxlengthReached = this.characterCount > this.maxLength;
      this.limitReached.emit(this.characterCount.toString());
    });
  }
}
